import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Award,
  BookOpen,
  CalendarDays,
  Infinity,
  Info,
  Loader2,
  Trophy,
} from "lucide-react";
import axios from "../../../../axios";
import { toast } from "react-toastify";

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const getArrayFromResponse = (response) => {
  const data = response?.data;
  const result = data?.result || data?.data;

  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.mockTests)) return result.mockTests;
  if (Array.isArray(data?.mockTests)) return data.mockTests;
  if (Array.isArray(data)) return data;

  return [];
};

const getMockTestId = (mockTest) =>
  pickFirst(
    mockTest?.mockTest?._id,
    mockTest?.mockTestId?._id,
    mockTest?.mockTestId,
    mockTest?.testId?._id,
    mockTest?.testId,
    mockTest?._id,
  );

const getCategory = (mockTest) =>
  pickFirst(
    mockTest?.category,
    mockTest?.course?.planName,
    mockTest?.courseName,
    mockTest?.examName,
    mockTest?.subject,
    "My Test",
  );

const getMockTestSource = (mockTest) =>
  pickFirst(mockTest?.mockTest, mockTest?.mockTestId, mockTest?.testId, mockTest);

const formatTimeValue = (value) => {
  if (!value) return "";

  const trimmed = String(value).trim();
  const timeParts = trimmed.match(/^(\d{1,2}):(\d{2})/);

  if (timeParts) {
    const hours = Number(timeParts[1]);
    const minutes = timeParts[2];
    const suffix = hours >= 12 ? "PM" : "AM";
    const displayHour = hours % 12 || 12;

    return `${displayHour}:${minutes} ${suffix}`;
  }

  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return trimmed;

  return date.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const formatStartDate = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
  });
};

const getStartChipData = (mockTest) => {
  const source = getMockTestSource(mockTest) || {};
  const startTime = pickFirst(mockTest?.startTime, source?.startTime, mockTest?.scheduledAt, source?.scheduledAt);
  const startDate = pickFirst(mockTest?.startDate, source?.startDate);
  const hasStartDateTime = Boolean(startDate || startTime);

  return {
    date: formatStartDate(startDate),
    time: formatTimeValue(startTime),
    hasStartDateTime,
  };
};

const normalizeTypeValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const getMockTestTabType = (mockTest) => {
  const source = getMockTestSource(mockTest) || {};
  const values = [
    mockTest?.testType,
    mockTest?.mockTestType,
    mockTest?.type,
    mockTest?.availabilityType,
    mockTest?.accessType,
    mockTest?.scheduleType,
    mockTest?.status,
    source?.testType,
    source?.mockTestType,
    source?.type,
    source?.availabilityType,
    source?.accessType,
    source?.scheduleType,
    source?.status,
  ].map(normalizeTypeValue);

  const hasLiveValue = values.some((value) =>
    ["live", "scheduled", "schedule", "scheduled_test", "live_test"].includes(value),
  );
  const hasFullTimeValue = values.some((value) =>
    ["permanent", "full_time", "fulltime", "full_time_test", "permanent_test"].includes(value),
  );

  if (
    mockTest?.isScheduled ||
    source?.isScheduled ||
    mockTest?.scheduledAt ||
    source?.scheduledAt ||
    mockTest?.startDate ||
    source?.startDate ||
    hasLiveValue
  ) {
    return "live";
  }

  if (mockTest?.isPermanent || source?.isPermanent || hasFullTimeValue) {
    return "fullTime";
  }

  return "fullTime";
};

const MyTestsAll = () => {
  const navigate = useNavigate();
  const [mockTests, setMockTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTestTab, setActiveTestTab] = useState("live");

  useEffect(() => {
    const fetchMockTests = async () => {
      setLoading(true);

      try {
        const response = await axios.get("/institute/mock-tests");
        setMockTests(getArrayFromResponse(response));
      } catch (error) {
        toast.error("Unable to load my tests");
      } finally {
        setLoading(false);
      }
    };

    fetchMockTests();
  }, []);

  const filteredMockTests = useMemo(
    () =>
      mockTests.filter((mockTest) => getMockTestTabType(mockTest) === activeTestTab),
    [mockTests, activeTestTab],
  );

  const totalTests = filteredMockTests.length;

  return (
    <div className="min-h-screen bg-[#fbfafc] pb-24 text-[#211629]">
      <main className="w-full px-4 py-6 sm:px-6 lg:mt-10 lg:px-10 xl:px-12">
        <section className="flex w-full max-w-[620px] items-center gap-4 rounded-[1.35rem] bg-[#562566] px-4 py-5 text-left text-white shadow-[0_12px_24px_rgba(86,37,102,0.16)] lg:max-w-none lg:px-8 lg:py-5">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/14">
            <BookOpen className="h-7 w-7" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-extrabold leading-tight sm:text-xl">
              My Test
            </h2>
            <p className="mt-1 text-xs font-medium leading-5 text-white/82 sm:text-sm">
              Institute assigned mock tests.
            </p>
          </div>
        </section>

        <div className="mt-5 grid w-full max-w-[620px] grid-cols-2 gap-2 rounded-[1.25rem] border border-[#ebe4ef] bg-white p-1.5 shadow-sm lg:max-w-none lg:gap-3 lg:p-2">
          {[
            {
              key: "live",
              label: "Live Test",
              icon: CalendarDays,
            },
            {
              key: "fullTime",
              label: "Full Time Test",
              icon: Infinity,
            },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTestTab === tab.key;

            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTestTab(tab.key)}
                className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-[1rem] px-3 text-sm font-extrabold transition ${
                  isActive
                    ? "bg-[#562566] text-white shadow-[0_10px_18px_rgba(86,37,102,0.18)]"
                    : "text-[#562566] hover:bg-[#f7effa]"
                } lg:min-h-14 lg:text-base`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="mt-6 flex min-h-[260px] items-center justify-center rounded-[1.6rem] border border-[#eee8f0] bg-white">
            <Loader2 className="h-9 w-9 animate-spin text-[#562566]" />
          </div>
        ) : totalTests ? (
          <section className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {filteredMockTests.map((mockTest, index) => {
              const mockTestId = getMockTestId(mockTest) || index;
              const title = pickFirst(
                mockTest?.title,
                mockTest?.name,
                mockTest?.mockTestName,
                mockTest?.mockTest?.name,
                "My Test",
              );
              const totalQuestions = pickFirst(
                mockTest?.totalQuestions,
                mockTest?.questionCount,
                mockTest?.questions?.length,
                0,
              );
              const duration = pickFirst(
                mockTest?.durationMinutes,
                mockTest?.duration,
                mockTest?.timeLimit,
                0,
              );
              const marks = pickFirst(
                mockTest?.totalMarks,
                mockTest?.marks,
                mockTest?.maxMarks,
                totalQuestions,
              );
              const startChip = getStartChipData(mockTest);
              const startLabel =
                activeTestTab === "fullTime" && !startChip.hasStartDateTime
                  ? "Start Now"
                  : `${startChip.date || "--"} ${startChip.time || ""}`.trim();
              const statusLabel = mockTest?.isSubmitted
                ? "Completed"
                : activeTestTab === "live"
                  ? "Upcoming"
                  : "Available";
              const statusClass = mockTest?.isSubmitted
                ? "bg-[#e9eef8] text-[#3F5F9A]"
                : "bg-[#F8B028] text-[#6b4900]";

              return (
                <article
                  key={mockTestId}
                  className="relative overflow-hidden rounded-[1.35rem] border border-[#E5DDF3] bg-gradient-to-br from-[#f8f5fb] to-[#eee9f5] text-left shadow-[0_12px_28px_rgba(75,45,131,0.14)]"
                >
                  <div className="p-4 pb-0">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="min-w-0 break-words text-base font-extrabold leading-tight text-[#211629]">
                        {title}
                      </h3>
                      <span className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-bold ${statusClass}`}>
                        {statusLabel}
                      </span>
                    </div>

                    <div className="mt-4 flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => navigate(`/dash/my-test-details/${mockTestId}`)}
                        className="inline-flex items-center gap-2 text-xs font-semibold text-[#3F234E]"
                      >
                        View Test Rules
                        <Info className="h-3.5 w-3.5" />
                      </button>
                      <p className="shrink-0 text-xs font-bold text-[#211629]">
                        Duration : {duration} min
                      </p>
                    </div>

                    <div className="mt-5 flex items-center justify-between gap-4">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FFF3DD] text-[#F5A623]">
                          <Trophy className="h-5 w-5" />
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-extrabold leading-none text-[#211629]">
                            {totalQuestions}
                          </p>
                          <p className="mt-1 text-[10px] font-semibold text-[#6e6875]">
                            Total Questions
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => navigate(`/dash/my-test-details/${mockTestId}`)}
                        className="flex min-h-[58px] w-[102px] shrink-0 flex-col items-center justify-center rounded-xl bg-[#3F234E] px-3 text-center text-sm font-extrabold leading-tight text-white shadow-[0_10px_18px_rgba(63,35,78,0.22)]"
                      >
                        View
                        <span className="text-[11px] font-bold">Details</span>
                      </button>
                    </div>

                    <div className="mt-4 flex items-center justify-between gap-3 text-xs font-semibold text-[#FF3A3A]">
                      <span>{totalQuestions} Questions</span>
                      <span>{marks} Marks</span>
                    </div>

                    <div className="mt-2 h-1 rounded-full bg-[#d5cfdd]">
                      <div className="h-full w-full rounded-full bg-[#F8B028]" />
                    </div>

                    <div className="mt-3 rounded-xl bg-white/85 px-4 py-4 shadow-[0_10px_22px_rgba(75,45,131,0.12)]">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="text-xs font-bold text-[#F5A623]">
                            {getCategory(mockTest)}
                          </p>
                          <p className="mt-2 text-sm font-extrabold text-[#562566]">
                            Credits {marks}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-semibold text-[#6e6875]">Starts</p>
                          <p className="mt-2 text-sm font-extrabold text-[#562566]">
                            {startLabel}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => navigate(`/dash/my-test-details/${mockTestId}`)}
                    className="mt-3 flex min-h-[42px] w-full items-center justify-between gap-3 bg-[#3F234E] px-4 py-2 text-xs font-extrabold text-white"
                  >
                    <span className="inline-flex items-center gap-2">
                      <Award className="h-4 w-4 text-white/90" />
                      My Test
                    </span>
                    <span className="inline-flex items-center gap-1 text-[#F8B028]">
                      View Details
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </button>
                </article>
              );
            })}
          </section>
        ) : (
          <div className="mt-6 rounded-[1.6rem] border border-[#eee8f0] bg-white px-5 py-10 text-center shadow-sm">
            <h2 className="text-xl font-extrabold text-[#211629]">
              {activeTestTab === "live"
                ? "No live tests available"
                : "No full time tests available"}
            </h2>
            <p className="mt-2 text-sm font-medium text-gray-500">
              {activeTestTab === "live"
                ? "Abhi scheduled tests assign nahi hue hain."
                : "Abhi permanent tests assign nahi hue hain."}
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default MyTestsAll;
